import axios from "axios";
import {
  API_BASE_NOTES_URL,
  API_NOTES_LIST,
  API_NOTES_DETAILS,
  API_NOTES_CREATE,
  API_NOTES_UPDATE,
  API_NOTES_DELETE,
  API_NOTES_BY_PATIENT,
  API_NOTES_SEARCH,
  API_NOTES_ARCHIVE,
} from "../../../helpers/urlHelper";

// Configurar instancia base de axios
const api = axios.create({
  baseURL: API_BASE_NOTES_URL,
  timeout: 10000,
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ========================================================================
// 📝 SERVICIOS DE NOTAS
// ========================================================================

class NoteService {
  
  // Obtener notas de un paciente específico
  static async getNotesByPatient(patientId, params = {}) {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await api.get(
        `${API_NOTES_BY_PATIENT}/${patientId}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      console.error("Error al obtener notas del paciente:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al cargar las notas del paciente"
      );
    }
  }

  // Buscar notas de un paciente
  static async searchNotes(patientId, searchParams = {}) {
    try {
      const queryParams = new URLSearchParams(searchParams);
      const response = await api.get(
        `${API_NOTES_SEARCH}/${patientId}/search?${queryParams}`
      );
      return response.data;
    } catch (error) {
      console.error("Error al buscar notas:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al buscar notas"
      );
    }
  }

  // Crear nueva nota
  static async createNote(noteData) {
    try {
      const response = await api.post(API_NOTES_CREATE, noteData);
      return response.data;
    } catch (error) {
      console.error("Error al crear nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al crear la nota"
      );
    }
  }

  // Obtener nota por ID
  static async getNoteById(noteId) {
    try {
      const response = await api.get(`${API_NOTES_DETAILS}/${noteId}`);
      return response.data;
    } catch (error) {
      console.error("Error al obtener nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al cargar la nota"
      );
    }
  }

  // Actualizar nota
  static async updateNote(noteId, noteData) {
    try {
      const response = await api.put(`${API_NOTES_UPDATE}/${noteId}`, noteData);
      return response.data;
    } catch (error) {
      console.error("Error al actualizar nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al actualizar la nota"
      );
    }
  }

  // Eliminar nota (soft delete)
  static async deleteNote(noteId) {
    try {
      const response = await api.delete(`${API_NOTES_DELETE}/${noteId}`);
      return response.data;
    } catch (error) {
      console.error("Error al eliminar nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al eliminar la nota"
      );
    }
  }

  // Archivar nota
  static async archiveNote(noteId) {
    try {
      const response = await api.patch(`${API_NOTES_ARCHIVE}/${noteId}/archive`);
      return response.data;
    } catch (error) {
      console.error("Error al archivar nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al archivar la nota"
      );
    }
  }

  // Obtener estadísticas de notas de un paciente
  static async getNoteStats(patientId) {
    try {
      const response = await api.get(
        `${API_NOTES_BY_PATIENT}/${patientId}/stats`
      );
      return response.data;
    } catch (error) {
      console.error("Error al obtener estadísticas de notas:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al cargar estadísticas"
      );
    }
  }

  // Duplicar nota
  static async duplicateNote(noteId) {
    try {
      const response = await api.post(`${API_NOTES_DETAILS}/${noteId}/duplicate`);
      return response.data;
    } catch (error) {
      console.error("Error al duplicar nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al duplicar la nota"
      );
    }
  }

  // Restaurar nota archivada
  static async restoreNote(noteId) {
    try {
      const response = await api.patch(`${API_NOTES_ARCHIVE}/${noteId}/restore`);
      return response.data;
    } catch (error) {
      console.error("Error al restaurar nota:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al restaurar la nota"
      );
    }
  }

  // Obtener notas archivadas de un paciente
  static async getArchivedNotes(patientId, params = {}) {
    try {
      const queryParams = new URLSearchParams({ ...params, archived: true });
      const response = await api.get(
        `${API_NOTES_BY_PATIENT}/${patientId}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      console.error("Error al obtener notas archivadas:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al cargar notas archivadas"
      );
    }
  }

  // Exportar notas de un paciente
  static async exportNotes(patientId, format = 'pdf') {
    try {
      const response = await api.get(
        `${API_NOTES_BY_PATIENT}/${patientId}/export`,
        {
          params: { format },
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error al exportar notas:", error);
      throw new Error(
        error.response?.data?.message || 
        "Error al exportar notas"
      );
    }
  }
}

export default NoteService;